// pages/CheckoutPage.jsx - COMPLETE FIXED VERSION
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, Paper, Typography, TextField, 
  Button, Divider, List, ListItem, 
  ListItemText, Radio, RadioGroup, 
  FormControlLabel, FormLabel, Box,
  Chip, Checkbox, Alert, Grid,
  Dialog, DialogTitle, DialogContent,
  DialogActions, Accordion, AccordionSummary,
  AccordionDetails, CircularProgress
} from '@mui/material';
import { 
  LocalOffer, Savings, Loyalty, 
  Info as InfoIcon, CreditCard as CreditCardIcon,
  Lock as LockIcon, Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon, Payment as PaymentIcon,Phone as PhoneIcon
} from '@mui/icons-material';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement
} from '@stripe/react-stripe-js';
import noPhoto from "../assets/no_photo.webp";
import keywordsData from '../lang/keywords.json';
import GoogleMapModal from "../components/GoogleMapModal";
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { formatPrice } from '../components/CarrancyTrans';

// RETO Cafe Black & Yellow Theme Styles
const styles = {
  actionButton: {
    backgroundColor: '#FFD700',
    color: '#000000',
    borderRadius: '20px',
    px: 3,
    py: 1,
    textTransform: 'none',
    fontWeight: 700,
    marginTop: 5,
    '&:hover': {
      backgroundColor: '#FFE55C',
      boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
    }
  },
  lcathionactionButton: {
    backgroundColor: 'transparent',
    color: '#FFD700',
    border: '1.5px solid #FFD700',
    borderRadius: '40px',
    px: 1,
    py: 1,
    textTransform: 'none',
    fontWeight: 600,
    minWidth: '48px',
    '&:hover': {
      backgroundColor: '#FFD700',
      color: '#000000',
      borderColor: '#FFD700',
    }
  }
};

// Stripe Elements styling
const stripeElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#FFFFFF',
      '::placeholder': {
        color: '#B3B3B3',
      },
      iconColor: '#FFD700',
    },
    invalid: {
      color: '#FF3D00',
      iconColor: '#FF3D00',
    },
  },
};

// Stripe payment form component
const StripePaymentForm = ({ 
  clientSecret, 
  onSuccess, 
  onError, 
  onCancel,
  language,
  totalAmount,
  cartCurrency = 'MYR'
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState({
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: elements.getElement(CardNumberElement),
        billing_details: {
          name: 'Card Holder',
        },
      }
    });

    if (error) {
      onError(error.message);
    } else if (paymentIntent.status === 'succeeded') {
      onSuccess(paymentIntent);
    }

    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1 }}>
            {language === 'ar' ? 'رقم البطاقة' : 'Card Number'}
          </Typography>
          <Box sx={{
            backgroundColor: '#1A1A1A',
            borderRadius: '8px',
            p: 2,
            border: '1px solid #2D2D2D',
            '&:hover': { borderColor: '#FFD700' }
          }}>
            <CardNumberElement 
              options={{
                ...stripeElementOptions,
                showIcon: true,
                placeholder: '4111 1111 1111 1111'
              }}
              onChange={(e) => setCardComplete({ ...cardComplete, cardNumber: e.complete })}
            />
          </Box>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1 }}>
            {language === 'ar' ? 'تاريخ الانتهاء' : 'Expiry Date'}
          </Typography>
          <Box sx={{
            backgroundColor: '#1A1A1A',
            borderRadius: '8px',
            p: 2,
            border: '1px solid #2D2D2D',
            '&:hover': { borderColor: '#FFD700' }
          }}>
            <CardExpiryElement 
              options={stripeElementOptions}
              onChange={(e) => setCardComplete({ ...cardComplete, cardExpiry: e.complete })}
            />
          </Box>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1 }}>
            CVC
          </Typography>
          <Box sx={{
            backgroundColor: '#1A1A1A',
            borderRadius: '8px',
            p: 2,
            border: '1px solid #2D2D2D',
            '&:hover': { borderColor: '#FFD700' }
          }}>
            <CardCvcElement 
              options={stripeElementOptions}
              onChange={(e) => setCardComplete({ ...cardComplete, cardCvc: e.complete })}
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              fullWidth
              onClick={onCancel}
              sx={{ color: '#B3B3B3' }}
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={!cardComplete.cardNumber || !cardComplete.cardExpiry || !cardComplete.cardCvc || processing}
              startIcon={processing ? <CircularProgress size={20} /> : <LockIcon />}
              sx={{
                backgroundColor: '#FFD700',
                color: '#000000',
                '&:hover': { backgroundColor: '#FFE55C' }
              }}
            >
              {processing 
                ? (language === 'ar' ? 'جاري المعالجة...' : 'Processing...')
                : (language === 'ar' ? `دفع ${formatPrice(totalAmount,cartCurrency,language)} ر.م` : `Pay ${formatPrice(totalAmount,cartCurrency,language)}`)
              }
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Alert 
        severity="info"
        icon={<SecurityIcon />}
        sx={{ 
          mt: 2,
          backgroundColor: 'rgba(0, 100, 0, 0.1)',
          color: '#4CAF50',
          border: '1px solid #4CAF50'
        }}
      >
        {language === 'ar' 
          ? 'جميع معلومات الدفع مشفرة وآمنة بواسطة Stripe. لن يتم تخزين بيانات بطاقتك.'
          : 'All payment information is encrypted and secure with Stripe. Your card data is not stored.'
        }
      </Alert>
    </form>
  );
};

// Main checkout page component
const CheckoutPage = ({keywordsData, language}) => {
  const { 
    cart, 
    cartTotal, 
    totalSavings, 
    clearCart,
    getItemPrice,
    getOriginalPrice,
    hasDiscount,
    deliverFees,
    serviceChargePercent,
    serviceChargeValue,
    cartCurrency
  } = useCart();
  
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Refs
  const debounceTimeoutRef = React.useRef(null);
  
  // Payment related states
  const [paymentMethod, setPaymentMethod] = useState('cash_on_delivery');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [isPhoneVerified, setIsPhoneVerified] = useState(user?.is_phone_verified || false);
  const [showVerification, setShowVerification] = useState(false);
  
  // Stripe specific states
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [currentOrder, setCurrentOrder] = useState(null);

  // Location states
  const [shippingAddress, setShippingAddress] = useState('');
  const [shippingPoint, setShippingPoint] = useState({ longitude: 0.0 , latitude: 0.0 });
  const [tableId, setTableId] = useState(Number(localStorage.getItem('tableId')) || 0);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [isSelectMap, setIsSelectMap] = useState(false);
  const [location, setLocation] = useState({ lat: null, lng: null });
  const [selectedLocation, setSelectedLocation] = useState(null);
  
  // Points states
  const [isProcessing, setIsProcessing] = useState(false);
  const [usePoints, setUsePoints] = useState(false);
  const [userPoints, setUserPoints] = useState(0);
  const [pointsError, setPointsError] = useState('');
  const [pointValueInt, setPointValueInt] = useState(0.5); // Renamed to avoid conflict

  // Check location state
  const [isCheackLocation, SetIsCheackLocation] = useState(() => {
    const checkIsLocated = localStorage.getItem('is_check_location') === 'true';
    if(checkIsLocated) {
      try { 
        const savedLocation = JSON.parse(localStorage.getItem("user_location"));
        setShippingPoint({longitude:savedLocation.lng,latitude:savedLocation.lat});
      } catch(error) {
        return false;
      }
    }
    return checkIsLocated;
  });

  // Initialize query from saved location or table
  useEffect(() => {
    const savedLocation = JSON.parse(localStorage.getItem("user_location") || 'null');
    if(tableId) {
      setShippingAddress('Table number : '+ tableId);
    } else if(savedLocation) {
      setShippingAddress(savedLocation.address);
      setQuery(savedLocation.address);
    }
  }, [tableId]);

  // Fetch payment methods on mount
  useEffect(() => {
    fetchPaymentMethods();
  }, [cart]);

  const fetchPaymentMethods = async () => {
    try {
      const restID = localStorage.getItem('current_rest_id');
      const response = await api.get(`/payments/methods?resturantID=${restID}`);
      setPaymentMethods(response.data.methods || []);
      
      const stripeMethod = response.data.methods?.find(m => m.gateway === 'stripe');
      if (stripeMethod) {
        const stripeKey = stripeMethod.is_test_mode 
          ? 'pk_test_your_test_key'
          : stripeMethod.api_key;
        setStripePromise(loadStripe(stripeKey));
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const fetchPlaces = async (input) => {
    if(isSelectMap) return;
    
    try {
      const response = await api.get(`/autocomplete?input=${input}&language=${language}`);
      const data = response.data;
      if (data.predictions) {
        setSuggestions(data.predictions.map((place) => place.description));
      }
    } catch (error) {
      console.error('Error fetching places:', error);
    }
  };

  const loadGoogleMapsAPI = (callback) => {
    if (window.google && window.google.maps) {
      callback();
      return;
    }
  
    if (!document.getElementById("google-maps-script")) {
      const script = document.createElement("script");
      const api = 'AIzaSyBl9P55wDymA5tQzFgWFaZWgMyQMdOSWXA';
      script.id = "google-maps-script";
      script.src = `https://maps.googleapis.com/maps/api/js?key=${api}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log("Google Maps API Loaded");
        callback();
      };
      script.onerror = () => {
        console.error("Failed to load Google Maps API.");
      };
      document.body.appendChild(script);
    } else {
      setTimeout(() => {
        if (window.google && window.google.maps) {
          callback();
        }
      }, 1000);
    }
  };
  
  const handleLocationSelect = (location) => {
    if (!window.google || !window.google.maps) {
      loadGoogleMapsAPI(() => handleLocationSelect(location));
      return;
    }
    
    if(location.address) {
      setQuery(location.address);
      localStorage.setItem('user_location',JSON.stringify(location));
      setShippingPoint({longitude:location.lng,latitude:location.lat});
      setShippingAddress(location.address);
      SetIsCheackLocation(true);
      setIsSelectMap(true);
    } else {
      setQuery(location);
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: location }, (results, status) => {
        if (status === "OK" && results[0]) {
          const locationSet = results[0].geometry.location;
          setSelectedLocation({
            lat: locationSet.lat(),
            lng: locationSet.lng(),
            address: location,
          });
          setOpen(true);
        }
      });
    }
    setSuggestions([]);
  };

  const handleAutoDetect = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setSelectedLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setOpen(true);
      },
      (error) => {
        alert(language === 'ar' ? keywordsData.enable_gps_ar : keywordsData.enable_gps_en);
      }
    );
  };

  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setShippingAddress(value);
    
    clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => {
      if (value.length > 2) {
        fetchPlaces(value);
      } else {
        setSuggestions([]);
      }
    }, 500);
  };

  // Fetch user points balance
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  useEffect(() => {
    const fetchUserPoints = async () => {
      if (user) {
        try {
          const response = await api.get('/payment/my-wallet');
          setUserPoints(response.data.points_balance || 0);
          setPointValueInt(response.data.points_value_int || 0.5);
          setIsPhoneVerified(response.data.is_phone_verified || false);
        } catch (err) {
          console.error('Error fetching user points:', err);
        }
      }
    };

    fetchUserPoints();
  }, [user]);

  // Calculate points value
  const calculatedPointsValue = userPoints * pointValueInt;
  const maxPointsToUse = Math.min(userPoints, Math.floor(cartTotal / pointValueInt));
  const pointsDiscount = usePoints ? maxPointsToUse * pointValueInt : 0;
  const finalTotal = Math.max(0, cartTotal - pointsDiscount);

  const handlePointsChange = (event) => {
    setUsePoints(event.target.checked);
    setPointsError('');
  };

  const handlePaymentMethodChange = (event) => {
    setPaymentMethod(event.target.value);
    const method = paymentMethods.find(m => m.gateway === event.target.value);
    setSelectedPaymentMethod(method);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (paymentMethod !== 'cash_on_delivery') {
      setIsProcessing(true);
      setPointsError('');

      if (usePoints && userPoints === 0) {
        setPointsError('You have no points to use');
        setIsProcessing(false);
        return;
      }

      try {
        const orderResponse = await api.post('/checkout', {
          shipping_address: shippingAddress,
          payment_method: paymentMethod,
          points_used: usePoints ? maxPointsToUse : 0,
          longitude: shippingPoint.longitude,
          latitude: shippingPoint.latitude,
          tableScan: tableId || null
        });

        setCurrentOrder(orderResponse.data);
        
        if (paymentMethod === 'stripe') {
          const paymentResponse = await api.post('/payments/process', {
            order_id: orderResponse.data.id,
            payment_method_id: selectedPaymentMethod.id,
            payment_details: {}
          });

          if (paymentResponse.data.clientSecret) {
            setClientSecret(paymentResponse.data.clientSecret);
            setPaymentDialogOpen(true);
          } else {
            throw new Error('Failed to initialize payment');
          }
        }
        
      } catch (err) {
        console.error('Checkout failed:', err);
        if (err.response?.data?.message === 'Insufficient points balance') {
          setPointsError('Insufficient points balance');
        } else if (err.response?.data?.message === 'Shop closed'){
          alert(language === 'ar' ? 'الكافي مغلق في الوقت الحالي الرجاء المحاولة لاحقا' : 'The cafe is currently closed. Please try again later.');
        } else {
          alert('Checkout failed. Please try again.');
        }
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    // Cash on delivery flow
    setIsProcessing(true);
    setPointsError('');

    if (usePoints && userPoints === 0) {
      setPointsError('You have no points to use');
      setIsProcessing(false);
      return;
    }

    try {
      const response = await api.post('/checkout', {
        shipping_address: shippingAddress,
        payment_method: paymentMethod,
        points_used: usePoints ? maxPointsToUse : 0,
        longitude: shippingPoint.longitude,
        latitude: shippingPoint.latitude,
        tableScan: tableId || null
      });

      clearCart();
      navigate(`/order-confirmation/${response.data.id}`);
    } catch (err) {
      console.error('Checkout failed:', err);
      if (err.response?.data?.message === 'Insufficient points balance') {
        setPointsError('Insufficient points balance');
      } else if (err.response?.data?.message === 'Shop closed'){
        alert(language === 'ar' ? 'الكافي مغلق في الوقت الحالي الرجاء المحاولة لاحقا' : 'The cafe is currently closed. Please try again later.');
      } else {
        alert('Checkout failed. Please try again.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStripeSuccess = async (paymentIntent) => {
    setPaymentResult({
      success: true,
      transaction: {
        transaction_id: paymentIntent.id,
        amount: paymentIntent.amount / 100
      },
      order: currentOrder
    });
    
    clearCart();
    setTimeout(() => {
      navigate(`/order-confirmation/${currentOrder.id}?payment=success`);
    }, 2000);
  };

  const handleStripeError = (error) => {
    setPaymentResult({
      success: false,
      error: error
    });
  };

  const carancyTxt = language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en;
  const prossisingTxt = language === 'ar' ? 'جار المعالجة ...' : 'Processing ...';
  const contenuWithPointsTxt = language === 'ar' ? 'أكمل الطلب بالنقاط' : 'Complete with points' ;
  const orderTxt = language === 'ar' ? 'إطلب ' : 'Order ';
  const saveTxt = language === 'ar' ? ' و وفر ' : ' and save ';
  
  const originalTotal = cart.reduce((total, item) => {
    return total + (getOriginalPrice(item) * item.quantity);
  }, 0);

  return (
    <Container maxWidth="md" sx={{ 
      py: 4, 
      mb: 10,
      backgroundColor: '#000000',
      minHeight: '100vh',
      color: '#FFFFFF'
    }}>
      <Typography variant="h4" gutterBottom sx={{ 
        color: '#FFD700', 
        fontWeight: 800,
        borderBottom: '3px solid #FFD700',
        pb: 1,
        mb: 3,
        display: 'inline-block'
      }}>
        {language === "ar" ? keywordsData.check_out_title_txt_ar : keywordsData.check_out_title_txt_en}
      </Typography>

      <Paper elevation={3} sx={{ 
        p: 3, 
        backgroundColor: '#0D0D0D',
        border: '1px solid #2D2D2D',
        borderRadius: '16px',
        '&:hover': {
          border: '1px solid #FFD700',
        }
      }}>
        <form onSubmit={handleSubmit}>
          {/* Delivery Address Section */}
          <Box sx={{ mb: 4 }}>
            <FormLabel component="legend" sx={{ 
              color: '#FFD700', 
              fontWeight: 600,
              mb: 1,
              fontSize: '1rem'
            }}>
              {language === "ar" ? keywordsData.deliver_adress_txt_ar : keywordsData.deliver_adress_txt_en}
            </FormLabel>
            {tableId === 0 ? (
            <div className="search-bar" style={{ position: 'relative' }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flexGrow: 1, position: 'relative' }}>
                  <input
                    className="input"
                    type="text"
                    placeholder={language === "ar" ? keywordsData.search_match_location_place_holder_ar : keywordsData.search_match_location_place_holder_en}
                    value={query}
                    onChange={handleQueryChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: '#1A1A1A',
                      border: '1px solid #2D2D2D',
                      borderRadius: '40px',
                      color: '#FFFFFF',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#FFD700'}
                    onBlur={(e) => e.target.style.borderColor = '#2D2D2D'}
                  />
                  
                  {suggestions.length > 0 && (
                    <Box sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: '#0D0D0D',
                      border: '1px solid #FFD700',
                      borderRadius: '12px',
                      mt: 1,
                      zIndex: 1000,
                      maxHeight: '300px',
                      overflowY: 'auto',
                      boxShadow: '0 8px 24px rgba(255, 215, 0, 0.15)',
                    }}>
                      {suggestions.map((place, index) => (
                        <Box
                          key={index}
                          onClick={() => handleLocationSelect(place)}
                          sx={{
                            p: 2,
                            cursor: 'pointer',
                            color: '#FFFFFF',
                            borderBottom: index < suggestions.length - 1 ? '1px solid #2D2D2D' : 'none',
                            '&:hover': {
                              backgroundColor: '#FFD700',
                              color: '#000000',
                            }
                          }}
                        >
                          {place}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                
                <Button 
                  variant="outlined" 
                  sx={{ ...styles.lcathionactionButton, ml: 2 }}
                  onClick={handleAutoDetect}
                >
                  <MyLocationIcon />
                </Button>
              </Box>
            </div>
            ) : (
              <div>
                   <input
                    className="input"
                    type="text"
                    placeholder={language === "ar" ? keywordsData.search_match_location_place_holder_ar : keywordsData.search_match_location_place_holder_en}
                    value={'Table number : '+ tableId}
                    required
                    disabled
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: '#1A1A1A',
                      border: '1px solid #2D2D2D',
                      borderRadius: '40px',
                      color: '#FFFFFF',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#FFD700'}
                    onBlur={(e) => e.target.style.borderColor = '#2D2D2D'}
                  />
              </div> 
            )}
          </Box>

          {/* Payment Method Section */}
          <Box sx={{ mb: 4 }}>
            <FormLabel component="legend" sx={{ 
              color: '#FFD700', 
              fontWeight: 600,
              mb: 1,
              fontSize: '1rem'
            }}>
              {language === "ar" ? keywordsData.payment_method_txt_ar : keywordsData.payment_method_txt_en}
            </FormLabel>
            <RadioGroup
              value={paymentMethod}
              onChange={handlePaymentMethodChange}
            >
              <FormControlLabel
                value="cash_on_delivery"
                control={<Radio sx={{
                  color: '#2D2D2D',
                  '&.Mui-checked': {
                    color: '#FFD700',
                  },
                }} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: '#FFFFFF' }}>
                      {language === "ar" ? keywordsData.payment_cash_txt_ar : keywordsData.payment_cash_txt_en}
                    </Typography>
                  </Box>
                }
              />

              {paymentMethods.map((method) => (
                <FormControlLabel
                  key={method.id}
                  value={method.gateway}
                  control={<Radio sx={{
                    color: '#2D2D2D',
                    '&.Mui-checked': {
                      color: '#FFD700',
                    },
                  }} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span style={{ fontSize: '1.5rem' }}>{method.icon}</span>
                        <Typography sx={{ color: '#FFFFFF' }}>
                          {language === 'ar' ? method.name_ar : method.name}
                        </Typography>
                      </Box>
                      {method.is_test_mode && (
                        <Chip
                          label={language === 'ar' ? 'وضع الاختبار' : 'Test Mode'}
                          size="small"
                          sx={{ backgroundColor: '#FFD700', color: '#000000' }}
                        />
                      )}
                    </Box>
                  }
                />
              ))}
            </RadioGroup>

            {paymentMethod !== 'cash_on_delivery' && selectedPaymentMethod && (
              <Alert 
                severity="info"
                sx={{ 
                  mt: 2,
                  backgroundColor: 'rgba(255, 215, 0, 0.1)',
                  color: '#FFFFFF',
                  border: '1px solid #FFD700'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LockIcon sx={{ color: '#FFD700' }} />
                  <Typography variant="body2">
                    {language === 'ar' 
                      ? paymentMethod === 'stripe'
                        ? 'مدعوم من Stripe - معلومات بطاقتك آمنة ومشفرة'
                        : 'معلومات بطاقتك مشفرة وآمنة 100%'
                      : paymentMethod === 'stripe'
                        ? 'Powered by Stripe - Your card information is secure and encrypted'
                        : 'Your card information is 100% encrypted and secure'
                    }
                  </Typography>
                </Box>
              </Alert>
            )}
          </Box>

{/* Points Redemption Section */}
{userPoints > 0 && (
  <Box sx={{ 
    mb: 4, 
    p: 3, 
    backgroundColor: '#0D0D0D', 
    border: '1px solid #2D2D2D',
    borderRadius: '12px',
    opacity: !isPhoneVerified ? 0.7 : 1,
    '&:hover': {
      border: !isPhoneVerified ? '1px solid #2D2D2D' : '1px solid #FFD700',
    }
  }}>
    <Typography variant="h6" gutterBottom sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 1,
      color: '#FFD700',
      fontWeight: 700
    }}>
      <Loyalty sx={{ color: '#FFD700' }} /> {language === "ar" ? keywordsData.point_earned_txt_ar : keywordsData.point_earned_txt_en}
      
      {/* Verification Warning */}
      {!isPhoneVerified && (
        <Chip
          icon={<PhoneIcon />}
          label={language === 'ar' ? 'يجب توثيق رقم الهاتف أولاً' : 'Phone verification required'}
          size="small"
          sx={{
            backgroundColor: '#FF9800',
            color: '#000000',
            fontWeight: 600,
            ml: 1
          }}
        />
      )}
    </Typography>
    
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
      <Checkbox
        checked={usePoints}
        onChange={handlePointsChange}
        disabled={!isPhoneVerified} // Disable if phone not verified
        sx={{
          color: '#2D2D2D',
          '&.Mui-checked': {
            color: '#FFD700',
          },
          '&.Mui-disabled': {
            color: '#6B6B6B',
          }
        }}
      />
      <Box>
        <Typography variant="body1" fontWeight="medium" sx={{ 
          color: !isPhoneVerified ? '#6B6B6B' : '#FFFFFF' 
        }}>
          {language === "ar" ? keywordsData.use_point_earned_txt_ar : keywordsData.use_point_earned_txt_en}
        </Typography>
        <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
          {language === "ar" ? keywordsData.you_have_txt_ar : keywordsData.you_have_txt_en} {userPoints}  {language === "ar" ? keywordsData.points_txt_ar : keywordsData.points_txt_en} ({formatPrice(calculatedPointsValue.toFixed(2),cartCurrency,language)})
        </Typography>
      </Box>
    </Box>

    {usePoints && (
      <Box sx={{ ml: 6 }}>
        <Alert 
          severity="info" 
          icon={<InfoIcon />} 
          sx={{ 
            mb: 2,
            backgroundColor: '#1A1A1A',
            color: '#FFFFFF',
            border: '1px solid #FFD700',
            '& .MuiAlert-icon': {
              color: '#FFD700',
            }
          }}
        >
          <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
            {language === "ar" ? ' إستعمال' : 'Use '} {maxPointsToUse} {language === "ar" ? keywordsData.points_txt_ar : keywordsData.points_txt_en} ({formatPrice(pointsDiscount.toFixed(2),cartCurrency,language)}) 
            {language === "ar" ? ' من الرصيد المتاح' : ' From available balance'}
          </Typography>
        </Alert>
        
        <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
         {language === 'ar' ? ' سيتم تطبيق النقاط كما يلي:' : 'Points will be applied as follow: '} <strong style={{ color: '#FFD700' }}>{formatPrice(pointsDiscount.toFixed(2),cartCurrency,language)} {language === "ar" ? 'تخفيض' : 'Discount'}</strong>
        </Typography>
        
        <Typography variant="caption" display="block" sx={{ color: '#6B6B6B', mt: 1 }}>
          {language === 'ar' ?  `* قيمة كل نقطة ${formatPrice(pointValueInt,cartCurrency,language)} . الحد الأقصى للنقاط المطبقة:  ` :  `* Each point is worth ${formatPrice(pointValueInt,cartCurrency,language)}. Maximum number of points applied:  `} {maxPointsToUse}
        </Typography>
      </Box>
    )}

    {pointsError && (
      <Alert severity="error" sx={{ 
        mt: 2,
        backgroundColor: '#1A1A1A',
        color: '#FF3D00',
        border: '1px solid #FF3D00',
      }}>
        {pointsError}
      </Alert>
    )}

    {/* Verification Required Message */}
    {!isPhoneVerified && (
      <Alert 
        severity="warning"
        icon={<PhoneIcon />}
        sx={{ 
          mt: 2,
          backgroundColor: 'rgba(255, 152, 0, 0.1)',
          color: '#FF9800',
          border: '1px solid #FF9800',
        }}
      >
        <Typography variant="body2">
          {language === 'ar' 
            ? 'يجب توثيق رقم هاتفك أولاً لتتمكن من استخدام رصيد النقاط' 
            : 'You need to verify your phone number first to use points balance'}
        </Typography>
        <Button
          size="small"
          onClick={() => navigate('../account')}
          sx={{
            mt: 1,
            color: '#FF9800',
            borderColor: '#FF9800',
            '&:hover': {
              borderColor: '#FFD700',
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
            }
          }}
          variant="outlined"
        >
          {language === 'ar' ? 'توثيق الآن' : 'Verify Now'}
        </Button>
      </Alert>
    )}
  </Box>
)}

          <Divider sx={{ 
            my: 2, 
            backgroundColor: '#2D2D2D',
            height: '1px'
          }} />

          {/* Order Summary */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ 
              color: '#FFD700',
              fontWeight: 700,
              mb: 2
            }}>
              {language === 'ar' ? 'ملخص الطلب' : 'Order Summary'}
            </Typography>
            
            {/* Savings Banner */}
            {totalSavings > 0 && (
              <Box sx={{ 
                backgroundColor: '#0D0D0D',
                border: '1px solid #FFD700',
                p: 2, 
                borderRadius: '12px', 
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Savings sx={{ color: '#FFD700' }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ 
                    color: '#FFD700', 
                    fontWeight: 'bold'
                  }}>
                   {language === 'ar' ? 'لقد وفرت ' : 'You save '} {formatPrice(totalSavings.toFixed(2),cartCurrency,language)} {language === 'ar' ? ' على هاذا الطلب!' : ' on this order'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                    {language === 'ar' ? 'عروض رائعة على المنتجات المختارة' : 'Great offers on selected products'}
                  </Typography>
                </Box>
              </Box>
            )}

            <List sx={{ 
              backgroundColor: '#0D0D0D',
              borderRadius: '12px',
              p: 2
            }}>
              {cart.map((item) => {
                const currentPrice = getItemPrice(item);
                const originalPrice = getOriginalPrice(item);
                const isDiscounted = hasDiscount(item);
                const discountPercent = isDiscounted 
                  ? Math.round((1 - currentPrice / originalPrice) * 100)
                  : 0;

                return (
                  <ListItem key={item.id} sx={{ 
                    alignItems: 'flex-start',
                    borderBottom: '1px solid #2D2D2D',
                    '&:last-child': {
                      borderBottom: 'none',
                    }
                  }}>
                    <Box sx={{ 
                      mr: 2, 
                      width: 60, 
                      height: 60,
                      backgroundColor: '#1A1A1A',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <img
                        src={item.product?.imageUrl || noPhoto}
                        alt={item.product?.name}
                        style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }}
                      />
                    </Box>
                    
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" fontWeight="medium" sx={{ color: '#FFFFFF' }}>
                        {language === 'ar' ? item.product?.name : item.product?.name_en}&nbsp;
                      </Typography>
                      
                      {isDiscounted ? (
                        <Box>
                          <Typography variant="body2" sx={{ 
                            color: '#FFD700', 
                            fontWeight: 'bold',
                            display: 'inline-block',
                            ml: 1
                          }}>
                            {formatPrice(currentPrice.toFixed(2),cartCurrency,language)} × {item.quantity}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              textDecoration: 'line-through', 
                              color: '#6B6B6B',
                              display: 'block'
                            }}
                          >
                            ${originalPrice.toFixed(2)} {language === "ar" ? 'كل' : 'each'}
                          </Typography>
                          <Chip
                            label={language === "ar" ? `وفر ${discountPercent}%` : `Save ${discountPercent}%`}
                            size="small"
                            sx={{
                              mt: 0.5,
                              backgroundColor: '#FFD700',
                              color: '#000000',
                              fontWeight: 'bold',
                              height: 20,
                              fontSize: '0.7rem'
                            }}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                          {formatPrice(currentPrice.toFixed(2),cartCurrency,language)} × {item.quantity}
                        </Typography>
                      )}
                    </Box>
                    
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ 
                      color: '#FFD700',
                      whiteSpace: 'nowrap',
                      mr: 2
                    }}>
                     {formatPrice((currentPrice * item.quantity).toFixed(2),cartCurrency,language)}
                    </Typography>
                  </ListItem>
                );
              })}
            </List>

            {/* Order Totals */}
            <Box sx={{ 
              mt: 2,
              p: 2,
              backgroundColor: '#0D0D0D',
              border: '1px solid #2D2D2D',
              borderRadius: '12px'
            }}>
              {/* Delivery Fees */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ color: '#B3B3B3' }}> {language === "ar" ? keywordsData.deliver_fees_txt_ar : keywordsData.deliver_fees_txt_en} :</Typography>
                <Typography sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                  {formatPrice(deliverFees.toFixed(2),cartCurrency,language)}
                </Typography>
              </Box>

              {/* Service charge */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ color: '#B3B3B3' }}> {language === 'ar' ? 'رسوم الخدمة ' : 'Service charge '} {serviceChargePercent.toFixed(2)}% :</Typography>
                <Typography sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                  {formatPrice(serviceChargeValue.toFixed(2),cartCurrency,language)}
                </Typography>
              </Box>

              {/* Payment fee if applicable */}
              {paymentMethod !== 'cash_on_delivery' && selectedPaymentMethod && selectedPaymentMethod.fee_percent > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ color: '#B3B3B3' }}>
                    {language === 'ar' ? 'رسوم المعاملة' : 'Transaction Fee'} ({selectedPaymentMethod.fee_percent}%):
                  </Typography>
                  <Typography sx={{ color: '#FFD700', fontWeight: 600 }}>
                    +{formatPrice((finalTotal * selectedPaymentMethod.fee_percent / 100).toFixed(2),cartCurrency,language)}
                  </Typography>
                </Box>
              )}

              {totalSavings > 0 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                      {language === "ar" ? 'السعر الإساسي :' : 'Origenal price :'}
                    </Typography>
                    <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#6B6B6B' }}>
                     {formatPrice(originalTotal.toFixed(2),cartCurrency,language)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#FFD700' }}>
                      <LocalOffer fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5, color: '#FFD700' }} />
                      {language === "ar" ? 'تخفيض :' : 'Discount :'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#FFD700' }}>
                      - {formatPrice(totalSavings.toFixed(2),cartCurrency,language)}
                    </Typography>
                  </Box>
                </>
              )}

              {usePoints && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#FFD700' }}>
                    <Loyalty fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5, color: '#FFD700' }} />
                    {language === "ar" ? 'تخفيض النقاط :' : 'Points Discount :'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#FFD700' }}>
                    - {formatPrice(pointsDiscount.toFixed(2),cartCurrency,language)}
                  </Typography>
                </Box>
              )}

              {(totalSavings > 0 || usePoints || (paymentMethod !== 'cash_on_delivery' && selectedPaymentMethod?.fee_percent > 0)) && (
                <Divider sx={{ my: 1, backgroundColor: '#2D2D2D' }} />
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
                  {language === "ar" ? 'الإجمالي :' : 'Total :'}
                </Typography>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: '#FFD700',
                    fontWeight: 900,
                    fontSize: '1.5rem'
                  }}
                >
                  {paymentMethod !== 'cash_on_delivery' && selectedPaymentMethod
                    ? formatPrice((finalTotal + (finalTotal * selectedPaymentMethod.fee_percent / 100)).toFixed(2),cartCurrency,language)
                    : formatPrice(finalTotal.toFixed(2),cartCurrency,language)
                  }
                </Typography>
              </Box>

              {(totalSavings > 0 || usePoints) && (
                <Typography variant="body2" sx={{ 
                  color: '#FFD700', 
                  textAlign: 'right', 
                  mt: 1,
                  fontWeight: 600
                }}>
                 {language === "ar" ? 'قيمة التخفيض: ' : 'Discount value: '} {formatPrice((totalSavings + pointsDiscount).toFixed(2),cartCurrency,language)}
                </Typography>
              )}
            </Box>

            {/* Item Count */}
            <Typography variant="body2" sx={{ 
              color: '#6B6B6B', 
              mt: 2, 
              textAlign: 'center' 
            }}>
              {cart.reduce((sum, item) => sum + item.quantity, 0)} {language === "ar" ? 'منتج في طلبك' : 'Product'}
            </Typography>
          </Box>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={isProcessing || cart.length === 0 || finalTotal <= 0}
            sx={{
              backgroundColor: (totalSavings > 0 || usePoints) ? '#FFD700' : '#FFD700',
              color: '#000000',
              fontWeight: 800,
              fontSize: '1.1rem',
              py: 1.8,
              borderRadius: '12px',
              '&:hover': {
                backgroundColor: '#FFE55C',
                boxShadow: '0 8px 20px rgba(255, 215, 0, 0.4)',
              },
              '&.Mui-disabled': {
                backgroundColor: '#2D2D2D',
                color: '#6B6B6B',
              }
            }}
          >
            {isProcessing ? prossisingTxt : (
              finalTotal === 0 ? contenuWithPointsTxt:
              `${orderTxt} ${(totalSavings > 0 || usePoints) ? `${saveTxt} ${formatPrice((totalSavings + pointsDiscount).toFixed(2),cartCurrency,language)}` : ''}`
            )}
          </Button>
          
          {finalTotal === 0 && (
            <Alert 
              severity="info" 
              sx={{ 
                mt: 2,
                backgroundColor: '#0D0D0D',
                color: '#FFD700',
                border: '1px solid #FFD700',
                '& .MuiAlert-icon': {
                  color: '#FFD700',
                }
              }}
            >
              {language === 'ar' ? 'سيتم دفع طلبك بالكامل باستخدام نقاطك المكتسبة!' : 'Your order will be fully paid for using your earned points!'}
            </Alert>
          )}
        </form>
      </Paper>

      {/* Stripe Payment Dialog */}
      <Dialog 
        open={paymentDialogOpen} 
        onClose={() => setPaymentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            border: '1px solid #FFD700',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ color: '#FFD700', fontWeight: 700, borderBottom: '1px solid #2D2D2D' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CreditCardIcon />
            <Typography>
              {language === 'ar' ? 'إدخال بيانات البطاقة' : 'Enter Card Details'}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {stripePromise && clientSecret && (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <StripePaymentForm
                clientSecret={clientSecret}
                onSuccess={handleStripeSuccess}
                onError={handleStripeError}
                onCancel={() => setPaymentDialogOpen(false)}
                language={language}
                totalAmount={(finalTotal + (finalTotal * (selectedPaymentMethod?.fee_percent || 0) / 100)).toFixed(2)}
                cartCurrency={cartCurrency}
              />
            </Elements>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Result Dialog */}
      <Dialog
        open={!!paymentResult}
        onClose={() => setPaymentResult(null)}
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            border: `1px solid ${paymentResult?.success ? '#4CAF50' : '#FF3D00'}`,
            borderRadius: '16px'
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', p: 4 }}>
          {paymentResult?.success ? (
            <>
              <CheckCircleIcon sx={{ fontSize: 60, color: '#4CAF50', mb: 2 }} />
              <Typography variant="h5" sx={{ color: '#4CAF50', fontWeight: 700, mb: 1 }}>
                {language === 'ar' ? 'تم الدفع بنجاح!' : 'Payment Successful!'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                {language === 'ar' 
                  ? `رقم المعاملة: ${paymentResult.transaction?.transaction_id}`
                  : `Transaction ID: ${paymentResult.transaction?.transaction_id}`
                }
              </Typography>
            </>
          ) : (
            <>
              <ErrorIcon sx={{ fontSize: 60, color: '#FF3D00', mb: 2 }} />
              <Typography variant="h5" sx={{ color: '#FF3D00', fontWeight: 700, mb: 1 }}>
                {language === 'ar' ? 'فشل الدفع' : 'Payment Failed'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                {paymentResult?.error}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', p: 3 }}>
          <Button
            variant="contained"
            onClick={() => {
              setPaymentResult(null);
              setPaymentDialogOpen(false);
              if (paymentResult?.success) {
                navigate(`/order-confirmation/${currentOrder?.id}?payment=success`);
              }
            }}
            sx={{
              backgroundColor: '#FFD700',
              color: '#000000',
              '&:hover': { backgroundColor: '#FFE55C' }
            }}
          >
            {language === 'ar' ? 'متابعة' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Google Maps Modal */}
      <GoogleMapModal 
        open={open} 
        handleClose={() => setOpen(false)} 
        onLocationSelect={handleLocationSelect} 
        initialCenter={selectedLocation} 
        language={language} 
        keywordsData={keywordsData}
      />
    </Container>
  );
};

export default CheckoutPage;